import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Trophy, Flame, Star, 
  TrendingUp, Zap, CheckCircle2,
  ChevronRight, RefreshCw,
  BookOpen, MessageCircle, Globe,
  Loader2, AlertTriangle, Shield, Users
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import Leaderboard from './Leaderboard';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

// =========================
// EXPERIENCE LEVELS CONFIG
// =========================

const EXPERIENCE_LEVELS = [
  { level: 1, name: "Explorer", xp_required: 0, color: "from-zinc-500 to-zinc-600" },
  { level: 2, name: "Adventurer", xp_required: 500, color: "from-blue-500 to-blue-600" },
  { level: 3, name: "Pathfinder", xp_required: 1500, color: "from-cyan-500 to-cyan-600" },
  { level: 4, name: "Challenger", xp_required: 3000, color: "from-green-500 to-green-600" },
  { level: 5, name: "Achiever", xp_required: 6000, color: "from-yellow-500 to-yellow-600" },
  { level: 6, name: "Trailblazer", xp_required: 10000, color: "from-orange-500 to-orange-600" },
  { level: 7, name: "Champion", xp_required: 15000, color: "from-red-500 to-red-600" },
  { level: 8, name: "Master", xp_required: 25000, color: "from-[#1EBFB3] to-[#1EBFB3]" },
  { level: 9, name: "Mentor", xp_required: 40000, color: "from-pink-500 to-pink-600" },
  { level: 10, name: "Legend", xp_required: 60000, color: "from-amber-400 via-amber-500 to-orange-500" },
];

// =========================
// XP & LEVEL CARD
// =========================

function XPLevelCard({ totalXP, level, levelName, xpToNextLevel, nextLevelXP, progressPercent }) {
  const levelConfig = EXPERIENCE_LEVELS.find(l => l.level === level) || EXPERIENCE_LEVELS[0];
  const isMaxLevel = level >= 10;
  
  return (
    <div className="bg-gradient-to-br from-navigate-gold/20 to-amber-600/10 border border-navigate-gold/30 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-14 h-14 bg-gradient-to-br ${levelConfig.color} rounded-xl flex items-center justify-center shadow-lg`}>
            <span className="text-2xl font-bold text-white">{level}</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">{levelName}</h3>
            <p className="text-sm text-navigate-gold/70">Level {level} of 10</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-navigate-gold">
            {totalXP.toLocaleString()}
          </div>
          <p className="text-sm text-zinc-400">Total XP</p>
        </div>
      </div>
      
      {!isMaxLevel && (
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-zinc-400">Progress to {EXPERIENCE_LEVELS[level]?.name || 'Next Level'}</span>
            <span className="text-navigate-gold">{xpToNextLevel.toLocaleString()} XP needed</span>
          </div>
          <div className="relative">
            <Progress value={progressPercent} className="h-3" />
            <div className="absolute right-0 top-0 -mt-1 text-xs text-zinc-500">
              {Math.round(progressPercent)}%
            </div>
          </div>
        </div>
      )}
      
      {isMaxLevel && (
        <div className="mt-4 flex items-center gap-2 text-navigate-gold">
          <Star className="h-5 w-5" />
          <span className="font-medium">Maximum level achieved! You are a Legend!</span>
        </div>
      )}
    </div>
  );
}

// =========================
// STREAK & STATS CARD
// =========================

function StreakStatsCard({ currentStreak, longestStreak, coursesCompleted }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
          <Flame className="h-6 w-6 text-orange-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Learning Streak</h3>
          <p className="text-sm text-zinc-400">Keep the momentum going!</p>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-orange-400">{currentStreak}</div>
          <div className="text-xs text-zinc-500 mt-1">Current Streak</div>
        </div>
        <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{longestStreak}</div>
          <div className="text-xs text-zinc-500 mt-1">Best Streak</div>
        </div>
        <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-green-400">{coursesCompleted}</div>
          <div className="text-xs text-zinc-500 mt-1">Courses Done</div>
        </div>
      </div>
      
      {currentStreak >= 7 && (
        <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
          <p className="text-sm text-orange-300 flex items-center gap-2">
            <Zap className="h-4 w-4" />
            {currentStreak >= 100 ? "Incredible! 100+ day streak!" :
             currentStreak >= 30 ? "Amazing! 30+ day streak!" :
             "Great job! Keep your streak going!"}
          </p>
        </div>
      )}
    </div>
  );
}

// =========================
// COURSES TO RETAKE
// =========================

function CoursesToRetake({ courses }) {
  const navigate = useNavigate();
  
  if (!courses || courses.length === 0) {
    return null;
  }
  
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
          <RefreshCw className="h-5 w-5 text-red-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Retake These Courses</h3>
          <p className="text-sm text-red-300/70">Complete these courses to pass</p>
        </div>
      </div>
      
      <div className="space-y-3">
        {courses.map((course) => (
          <div
            key={course.course_id}
            className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-xl border border-zinc-700 hover:border-red-500/50 transition-colors cursor-pointer"
            onClick={() => navigate(`/courses/${course.course_id}/ai-learn`)}
          >
            <div className="flex items-center gap-3">
              {course.thumbnail_url ? (
                <img 
                  src={course.thumbnail_url} 
                  alt={course.title}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-zinc-600" />
                </div>
              )}
              <div>
                <h4 className="font-medium text-white">{course.title}</h4>
                <p className="text-xs text-zinc-500">{course.duration || 'Self-paced'}</p>
              </div>
            </div>
            <button className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-colors">
              Retake
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =========================
// RECENT ACTIVITY
// =========================

function RecentActivityV2({ activities }) {
  const activityLabels = {
    account_registration: 'Account Created',
    complete_profile: 'Profile Completed',
    enrol_course: 'Enrolled in Course',
    complete_lesson_pass: 'Lesson Passed',
    complete_course_pass: 'Course Completed',
    complete_external_course: 'External Course Completed',
    attend_learning_lab: 'Learning Lab Attended',
    forum_participation: 'Forum Post',
    answer_question: 'Answered Question',
    receive_upvote: 'Received Upvote',
    complete_reflection_journal: 'Journal Entry',
    daily_login: 'Daily Login',
    streak_7_day: '7-Day Streak Bonus',
    streak_30_day: '30-Day Streak Bonus',
    streak_100_day: '100-Day Streak Bonus',
  };
  
  const activityIcons = {
    account_registration: Shield,
    complete_profile: Users,
    enrol_course: BookOpen,
    complete_lesson_pass: CheckCircle2,
    complete_course_pass: Trophy,
    complete_external_course: Globe,
    attend_learning_lab: BookOpen,
    forum_participation: MessageCircle,
    answer_question: MessageCircle,
    receive_upvote: TrendingUp,
    complete_reflection_journal: BookOpen,
    daily_login: Flame,
    streak_7_day: Flame,
    streak_30_day: Flame,
    streak_100_day: Flame,
  };
  
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
          <TrendingUp className="h-5 w-5 text-blue-400" />
        </div>
        <h3 className="text-lg font-bold text-white">Recent Activity</h3>
      </div>
      
      {activities && activities.length > 0 ? (
        <div className="space-y-2">
          {activities.slice(0, 8).map((activity, idx) => {
            const Icon = activityIcons[activity.activity] || CheckCircle2;
            const label = activityLabels[activity.activity] || activity.activity.replace(/_/g, ' ');
            
            return (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4 text-zinc-500" />
                  <span className="text-sm text-zinc-300">{label}</span>
                </div>
                <span className="text-sm text-navigate-gold font-medium">+{activity.xp_amount} XP</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-zinc-500 text-center py-4">No recent activity</p>
      )}
    </div>
  );
}

// =========================
// STREAK WIDGET (Compact Export)
// =========================

export function StreakWidget({ streak, className = "" }) {
  if (streak === 0) return null;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 border border-orange-500/30 rounded-full ${className}`}>
      <Flame className="h-4 w-4 text-orange-400" />
      <span className="text-sm font-medium text-orange-300">{streak} day streak</span>
    </div>
  );
}

// =========================
// XP DISPLAY (Compact Export)
// =========================

export function XPDisplay({ xp, className = "" }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 bg-navigate-gold/20 border border-navigate-gold/30 rounded-full ${className}`}>
      <Star className="h-4 w-4 text-navigate-gold" />
      <span className="text-sm font-medium text-navigate-gold">{xp.toLocaleString()} XP</span>
    </div>
  );
}

// =========================
// MAIN GAMIFICATION DASHBOARD (NO BADGES)
// =========================

export default function GamificationDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  
  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/gamification/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching gamification summary:', error);
      toast.error('Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSummary();
  }, [token]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }
  
  if (!summary) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">Failed to load gamification data</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header Stats Row */}
      <div className="grid md:grid-cols-2 gap-6">
        <XPLevelCard
          totalXP={summary.total_xp || 0}
          level={summary.level || 1}
          levelName={summary.level_name || 'Explorer'}
          xpToNextLevel={summary.xp_to_next_level || 500}
          nextLevelXP={summary.next_level_xp || 500}
          progressPercent={summary.progress_percent || 0}
        />
        <StreakStatsCard
          currentStreak={summary.current_streak || 0}
          longestStreak={summary.longest_streak || 0}
          coursesCompleted={summary.courses_completed || 0}
        />
      </div>
      
      {/* Courses to Retake - Only show if there are failed courses */}
      <CoursesToRetake courses={summary.courses_to_retake || []} />
      
      {/* Leaderboard Section */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-navigate-gold/20 rounded-lg flex items-center justify-center">
              <Trophy className="h-5 w-5 text-navigate-gold" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Leaderboard</h3>
              <p className="text-sm text-zinc-400">Top learners in your organization</p>
            </div>
          </div>
          <Link to="/leaderboard">
            <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:text-white">
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
        <Leaderboard compact={true} limit={10} />
      </div>
      
      {/* Recent Activity */}
      <RecentActivityV2 activities={summary.recent_activity || []} />
    </div>
  );
}
